import { useEffect, useRef, useState } from "react"
import bannersStore from "../../stores/bannersStore"
import { API_URL } from "../../services/constants"
import botStore from "../../stores/botStore"
import { useNavigate } from "react-router-dom"
import {
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    ShareAltOutlined,
} from "@ant-design/icons"
import ContextMenu from "./ContextMenu"
import { updateSortIds } from "../../services/bannerService"
import { toast } from "react-toastify"
import DeleteButton from "./DeleteModal"
import DeleteModal from "./DeleteModal"

export default function BannerBannerList() {
    const debounceTimeout = useRef()

    const navigate = useNavigate()
    const [draggedId, setDraggedId] = useState(null)
    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
    const [bannerToDelete, setBannerToDelete] = useState(null)

    const banners = bannersStore((state) => state.banners)
    const setBanners = bannersStore((state) => state.setBanners)
    const setSelectedBanner = bannersStore((state) => state.setSelectedBanner)
    const bot = botStore((state) => state.bot)
    const setBot = botStore((state) => state.setBot)

    const [autoChangeTime, setAutoChangeTime] = useState(null)
    const [contextMenu, setContextMenu] = useState({
        isOpen: false,
        x: 0,
        y: 0,
        banner: null,
    })

    // Handle right click
    const handleContextMenu = (e, banner) => {
        e.preventDefault() // Prevent default browser context menu
        e.stopPropagation()

        setContextMenu({
            isOpen: true,
            x: e.clientX,
            y: e.clientY,
            banner: banner,
        })
    }

    // Close context menu
    const closeContextMenu = () => {
        setContextMenu({ isOpen: false, x: 0, y: 0, bannerId: null })
    }

    // Context menu actions
    const handleDelete = (bannerId) => {
        // Add your delete logic here
        const updatedBanners = banners.filter((banner) => banner.id !== bannerId)
        setBanners(updatedBanners)
        closeContextMenu()
    }

    const handleEdit = (bannerId) => {
        const banner = banners.find((b) => b.id === bannerId)
        if (banner) {
            setSelectedBanner(banner)
            navigate("/banner-editor")
        }
        closeContextMenu()
    }

    const handleCopy = (bannerId) => {
        // Add duplicate logic here
        navigator.clipboard.writeText(`${API_URL}/banner/${bot.templateName}/${bannerId}`)

        closeContextMenu()
    }

    const handlePreview = (bannerId) => {
        // Add preview logic here
        const banner = banners.find((b) => b.id === bannerId)
        if (banner) {
            window.open(`${API_URL}/banner/${bot.templateName}/${bannerId}`, "_blank")
        }
        closeContextMenu()
    }

    // Context menu items
    const contextMenuItems = (banner) => [
        {
            label: "ویرایش",
            icon: <EditOutlined />,
            onClick: () => handleEdit(banner.id),
        },
        {
            label: "پیش نمایش",
            icon: <EyeOutlined />,
            onClick: () => handlePreview(banner.id),
        },
        {
            label: "کپی کردن",
            icon: <CopyOutlined />,
            onClick: () => handleCopy(banner.id),
        },
        {
            label: "حذف",
            icon: <DeleteOutlined style={{ color: "#ef4444" }} />,
            onClick: () => {
                setBannerToDelete(banner)
                setDeleteModalIsOpen(true)
            },
        },
    ]

    const handleDragStart = (e, id) => {
        setDraggedId(id)
        e.dataTransfer.effectAllowed = "move"
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
    }

    const handleDrop = (e, dropId) => {
        e.preventDefault()

        if (draggedId === dropId) return

        const draggedIdx = banners.findIndex((c) => c.id === draggedId)
        const dropIdx = banners.findIndex((c) => c.id === dropId)

        const newBanners = [...banners]
        const [removed] = newBanners.splice(draggedIdx, 1)
        newBanners.splice(dropIdx, 0, removed)

        // Update sort_id for all banners based on new position
        const updatedBanners = newBanners.map((banner, index) => ({
            ...banner,
            sort_id: index,
        }))

        setBanners(updatedBanners)

        // Save to database
        saveBannerOrder(updatedBanners)
    }

    const saveBannerOrder = async (updatedBanners) => {
        // Prepare data for database update
        const orderData = updatedBanners.map((banner) => ({
            id: banner.id,
            sort_id: banner.sort_id,
        }))

        const data = {
            orderData,
            autoChangeTime,
        }

        toast.promise(updateSortIds(data), {
            pending: "درحال ویرایش",
            success: "ویرایش شد",
        })
    }

    useEffect(() => {
        if (!autoChangeTime) return

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current)
            debounceTimeout.current = null
        }

        debounceTimeout.current = setTimeout(() => {
            updateSortIds({ autoChangeTime }).then(() => {
                toast.success("ویرایش شد")

                setBot({ ...bot, autoChangeTime: autoChangeTime })
            })
        }, 1000)
    }, [autoChangeTime])

    const handleDragEnd = () => {
        setDraggedId(null)
    }

    return (
        <div
            className="min-h-screen   p-8"
            style={{ direction: "rtl" }}
            onClick={closeContextMenu}
            onContextMenu={closeContextMenu}
        >
            <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                isOpen={contextMenu.isOpen}
                onClose={closeContextMenu}
                items={contextMenu.banner ? contextMenuItems(contextMenu.banner) : []}
            />
            <DeleteModal
                isOpen={deleteModalIsOpen}
                setIsOpen={setDeleteModalIsOpen}
                banner={bannerToDelete}
            />
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between">
                    <div>
                        <h1 className="text-4xl font-bold text-white mb-2">بنر های بات</h1>
                        <p className="text-purple-200 mb-8">
                            برای ویرایش ترتیب نمایش میتوانید هرکدوم را بکشید و رها کنید
                        </p>
                    </div>
                    <div>
                        <label for="switch-time">تغییر اتومات بنر هر x دقیقه</label>
                        <input
                            type="number"
                            onChange={(e) => setAutoChangeTime(e.target.value)}
                            placeholder="Default: 10"
                            defaultValue={bot.autoChangeTime}
                            class="block min-w-0 mt-2 grow bg-gray-800 py-1.5 pr-3 pl-1 text-base text-white placeholder:text-gray-500 focus:outline-none sm:text-sm/6"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {banners?.map((banner) => (
                        <div
                            key={banner.id}
                            draggable
                            onClick={() => {
                                setSelectedBanner(banner)
                                navigate("/banner-editor")
                            }}
                            onDragStart={(e) => handleDragStart(e, banner.id)}
                            onContextMenu={(e) => handleContextMenu(e, banner)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, banner.id)}
                            onDragEnd={handleDragEnd}
                            className={`relative rounded-xl shadow-lg p-6 cursor-move transition-all duration-200 hover:shadow-2xl hover:scale-105 ${
                                draggedId === banner.id ? "opacity-40 scale-95" : "opacity-100"
                            }`}
                            style={{
                                backgroundImage: `url(${banner.url})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                width: "360px",
                                height: "120px",
                            }}
                        >
                            {/* <div className="absolute top-1 left-0">
                                <div className="flex flex-col bg-cyan-500/10 backdrop-blur-sm rounded-lg border border-cyan-200/30">
                                    <button className="p-2 hover:bg-red-500/20 rounded-lg transition-colors group">
                                        <DeleteOutlined
                                            className="group-hover:text-red-600 transition-colors"
                                            style={{ color: "white", fontSize: "16px" }} // Changed to white for better visibility
                                        />
                                    </button>
                                    <button className="p-2 hover:bg-red-500/20 rounded-lg transition-colors group">
                                        <DeleteOutlined
                                            className="group-hover:text-red-600 transition-colors"
                                            style={{ color: "white", fontSize: "16px" }}
                                        />
                                    </button>
                                    <button className="p-2 hover:bg-red-500/20 rounded-lg transition-colors group">
                                        <DeleteOutlined
                                            className="group-hover:text-red-600 transition-colors"
                                            style={{ color: "white", fontSize: "16px" }}
                                        />
                                    </button>
                                </div>
                            </div> */}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
